import {
  Camera, Cog, Home as HomeIcon, KeyRound, Laptop, MapPin,
  MessageSquare, Network, Nfc, ScrollText, Sparkles, Tablet, Ticket,
  Users, Video, type LucideIcon,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Shell, useShellMeta } from './Shell.js'
import { Link } from '../router.js'
import { adminMutate, apiGet, usePermissions } from './primitives.js'
import { ageCle } from './cle-acces-model.js'
import { Field, NumberInput } from './form.js'
import { useSaveBar } from '../shell/editing.js'
import { fraicheurFr } from '../communication/lib.js'
import { SITE_FAMILIES } from '../navigation/site-navigation.js'
import { useIntegrationCatalog, IntegrationStatusPill, type IntegrationEntry } from '../navigation/integration-status.js'

/**
 * ADMINISTRATION → RÉGLAGES → INTÉGRATIONS (IA V2).
 *
 * TOUT le catalogue, groupé par les MÊMES familles que la navigation —
 * en TUILES (jamais un arbre ni un tableau, §89). Même langage visuel que
 * Communication → Intégrations, même état canonique (§92-93). Les
 * connecteurs « À intégrer » sont visibles et honnêtes : pas de bouton
 * Connecter tant qu'aucun flow n'existe (§158).
 */

const ICONS: Record<string, LucideIcon> = {
  shotgun: Ticket, instagram_market: Sparkles, instagram_stories: Sparkles,
  instagram_communication: Camera, instagram_tracker: Nfc, google_business: MapPin,
  facebook: MessageSquare, tiktok: Video, whatsapp: MessageSquare,
  skello: Users, unifi_access: KeyRound, unifi_protect: Video,
  main_courante: ScrollText, unifi_network: Network, home_assistant: HomeIcon,
  parc: Laptop, billetterie: Ticket, tablettes: Tablet,
}

function CapaciteLigne({ i }: { i: IntegrationEntry }) {
  if (i.capabilities.length === 0) return null
  return (
    <span className="ic-capline">
      {i.capabilities.map((c) => (
        <span key={c.label} className={`ic-cap ${c.active ? 'ic-cap--on' : ''}`}>
          {c.label} {c.active ? '✓' : '—'}
          {c.detail ? <em> · {c.detail}</em> : null}
        </span>
      ))}
    </span>
  )
}

function CarteIntegration({ i }: { i: IntegrationEntry }) {
  const Icon = ICONS[i.id] ?? Cog
  const corps = (
    <>
      <span className="lc-card__edge" aria-hidden="true" />
      <span className="lc-card__icon" aria-hidden="true"><Icon /></span>
      <div className="ic-card__texte">
        <h3>{i.name}</h3>
        <p className="lc-hint">{i.description}</p>
        {i.status_detail ? <p className="lc-hint ic-card__detail">{i.status_detail}</p> : null}
        {i.summary ? <p className="ic-card__resume">{i.summary}</p> : null}
        <CapaciteLigne i={i} />
        {/* §163 : seule une intégration qui SYNCHRONISE a cette ligne. */}
        {i.syncs && i.last_sync_at ? (
          <span className="lc-hint">Dernière synchronisation {fraicheurFr(i.last_sync_at)}</span>
        ) : null}
        {i.contract_id ? <span className="lc-hint">Contrat : {i.contract_id} — remplaçable par toute application conforme.</span> : null}
        <AgeCle i={i} />
      </div>
      <div className="ic-card__pied">
        <IntegrationStatusPill status={i.status} label={i.status_label} />
        {i.kind === 'LIVECLUB_SERVICE' ? <span className="lc-pill lc-pill--idle ic-interne">Interne</span> : null}
        {i.status === 'A_INTEGRER' ? null
          : <span className="ic-card__go">{i.kind === 'LIVECLUB_SERVICE' ? 'Ouvrir l’intégration →' : 'Configurer →'}</span>}
      </div>
    </>
  )
  // « À intégrer » : aucune destination, aucun faux CTA (§158).
  if (!i.config_route || i.status === 'A_INTEGRER') {
    return <div className="lc-card ic-card ic-card--muette lc-in">{corps}</div>
  }
  return <Link href={i.config_route} className="lc-card ic-card lc-in">{corps}</Link>
}

/*
 * ÂGE DE LA CLÉ D'ACCÈS — sur la carte même (15/10/2026).
 *
 * L'ancienne liste « âge des secrets » (MiscPages, puis supprimée) n'était
 * plus atteignable depuis le 31/08. Plutôt que de ressusciter une page morte,
 * l'âge s'affiche là où l'on clique pour agir : la carte mène au panneau qui
 * remplace la clé.
 */
function AgeCle({ i }: { i: IntegrationEntry }) {
  if (!i.cle) return null
  if (!i.cle.configuree) return <span className="lc-hint cle-age cle-age--manque">Aucune clé d’accès enregistrée</span>
  const r = i.cle.rotation
  // Au calendrier de Paris, pas en tranches de 24 h : une clé de la veille
  // n'est pas « changée aujourd'hui ».
  const age = i.cle.enregistree_le ? ageCle(i.cle.enregistree_le).carte : 'date inconnue'
  return (
    <span className="lc-hint cle-age" title={r.conseil}>
      Clé d’accès {age}
      {r.etat === 'EN_RETARD' ? <span className="lc-pill lc-pill--warning">à renouveler</span>
        : r.etat === 'A_PREVOIR' ? <span className="lc-pill lc-pill--idle">renouvellement à prévoir</span> : null}
    </span>
  )
}

interface ReglagesCles { rotation_jours: number; signaler_apres_jours: number; test_delai_s: number }
interface Bornes { rotation_jours: { min: number; max: number }; signaler_apres_jours: { min: number; max: number }; test_delai_s: { min: number; max: number } }

/**
 * RÉGLAGES DES CLÉS D'ACCÈS — règle de l'exploitant : chaque nouveauté a ses
 * réglages dans Administration. Les valeurs par défaut (365 j, 180 j, 8 s)
 * vivent dans le code ET dans la ligne posée par la migration.
 */
function ReglagesClesSection() {
  const permissions = usePermissions()
  const peutModifier = permissions.permissions.includes('integration.write')
  const [serveur, setServeur] = useState<ReglagesCles | null>(null)
  const [bornes, setBornes] = useState<Bornes | null>(null)
  const [saisie, setSaisie] = useState<Record<keyof ReglagesCles, string>>({ rotation_jours: '', signaler_apres_jours: '', test_delai_s: '' })

  const charger = useCallback(() => {
    void apiGet<{ reglages?: ReglagesCles; bornes?: Bornes }>('/api/v1/settings/integrations/cles/reglages').then((j) => {
      if (!j.reglages) return
      setServeur(j.reglages); setBornes(j.bornes ?? null)
      setSaisie({ rotation_jours: String(j.reglages.rotation_jours), signaler_apres_jours: String(j.reglages.signaler_apres_jours), test_delai_s: String(j.reglages.test_delai_s) })
    }).catch(() => undefined)
  }, [])
  useEffect(() => { charger() }, [charger])

  const n = (k: keyof ReglagesCles) => Number(saisie[k])
  const dirty = !!serveur && peutModifier && (Object.keys(saisie) as (keyof ReglagesCles)[]).some((k) => n(k) !== serveur[k])
  const horsBornes = !!bornes && (Object.keys(saisie) as (keyof ReglagesCles)[])
    .some((k) => !Number.isInteger(n(k)) || n(k) < bornes[k].min || n(k) > bornes[k].max)
  const invalide = horsBornes ? 'Une valeur sort des limites indiquées.'
    : n('signaler_apres_jours') >= n('rotation_jours') ? 'Le signalement doit commencer avant l’échéance de renouvellement.' : null

  useSaveBar({
    dirty,
    invalid: invalide,
    saveLabel: 'Enregistrer les réglages des clés',
    successMessage: 'Réglages des clés enregistrés.',
    onSave: async () => {
      const r = await adminMutate<{ error?: string; message?: string; reglages?: ReglagesCles }>(
        'PUT', '/api/v1/settings/integrations/cles/reglages',
        { rotation_jours: n('rotation_jours'), signaler_apres_jours: n('signaler_apres_jours'), test_delai_s: n('test_delai_s') })
      if (r.status !== 200 || !r.data.reglages) return r.data.message ?? 'Enregistrement refusé.'
      charger()
      return true
    },
    onCancel: () => {
      if (serveur) setSaisie({ rotation_jours: String(serveur.rotation_jours), signaler_apres_jours: String(serveur.signaler_apres_jours), test_delai_s: String(serveur.test_delai_s) })
    },
  })

  if (!serveur || !bornes) return null
  const champ = (k: keyof ReglagesCles, label: string, aide: string, unite: string) => (
    <Field label={label} helper={`${aide} (de ${bornes[k].min} à ${bornes[k].max} ${unite}).`}>
      {peutModifier
        ? <NumberInput value={saisie[k]} onChange={(v) => setSaisie((s) => ({ ...s, [k]: v }))} unit={unite}
            min={bornes[k].min} max={bornes[k].max} ariaLabel={label} width={160} />
        : <strong>{serveur[k]} {unite}</strong>}
    </Field>
  )
  return (
    <section className="lc-panel cle-panel cle-reglages" aria-labelledby="cles-reglages">
      <header className="lc-panel__head"><h2 className="lc-section-title" id="cles-reglages">Renouvellement des clés d’accès</h2></header>
      <div className="lc-panel__body cle-corps">
        <p className="lc-hint cle-intro">
          Gestion ne change jamais une clé tout seul : il signale celles qui vieillissent, et chaque
          carte ci-dessus mène au panneau qui permet de la remplacer sans couper la lecture.
        </p>
        <div className="cle-reglages__champs">
          {champ('rotation_jours', 'Conseiller de renouveler une clé après', 'Au-delà, la clé est marquée « à renouveler »', 'jours')}
          {champ('signaler_apres_jours', 'Commencer à le signaler après', 'Laisse le temps de s’organiser avant l’échéance', 'jours')}
          {champ('test_delai_s', 'Attente maximale lors d’un test de clé', 'Passé ce délai, le fournisseur est déclaré injoignable', 'secondes')}
        </div>
      </div>
    </section>
  )
}

export function IntegrationsCatalogPage() {
  const meta = useShellMeta()
  const catalogue = useIntegrationCatalog()
  const aRenouveler = (catalogue ?? []).filter((i) => i.cle && (!i.cle.configuree || i.cle.rotation.etat === 'EN_RETARD')).length

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger ic-page">
        <Link className="lc-crumb" href="/settings">← Réglages</Link>
        <header className="cm-head">
          <div>
            <h1 className="lc-title">Intégrations</h1>
            <p className="lc-subtitle">Connectez et supervisez les services utilisés par Gestion — un connecteur, un état, partout le même.</p>
            {aRenouveler > 0 ? (
              <p className="lc-hint">{aRenouveler === 1 ? 'Une clé d’accès est à renouveler ou manquante' : `${aRenouveler} clés d’accès sont à renouveler ou manquantes`} — voir les cartes marquées ci-dessous.</p>
            ) : null}
          </div>
        </header>
        {catalogue === null ? <p className="lc-hint">Chargement…</p> : null}
        {SITE_FAMILIES.map((fam) => {
          const entrees = (catalogue ?? []).filter((i) => i.family === fam.id)
          if (entrees.length === 0) return null
          return (
            <section key={fam.id} className="ic-groupe">
              <header className="ic-groupe__tete">
                <fam.icon size={17} aria-hidden="true" />
                <h2 className="lc-section-title">{fam.label}</h2>
                <span className="lc-hint">{fam.description}</span>
              </header>
              <div className="ic-grid">
                {entrees.map((i) => <CarteIntegration key={i.id} i={i} />)}
              </div>
            </section>
          )
        })}
        {catalogue !== null ? <ReglagesClesSection /> : null}
      </main>
    </Shell>
  )
}
